import {
  OrganizationSwitcher,
  useIdentityContext,
  useUserData,
} from '@flik/shared';
import { useTheme } from '@flik/shared/components';
import React, { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import trayIconPng from '/icons/flik-tray-icon.png';

interface NavBarProps {
  className?: string;
}

const NavBar: React.FC<NavBarProps> = ({ className = '' }) => {
  const {
    isLoggedIn,
    signOut,
    currentOrganization,
    availableOrganizations,
    switchOrganization,
  } = useIdentityContext();
  const { provideTailwind } = useTheme();
  const { userData, displayName, initials, whoamiData } = useUserData();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSignOutClick = () => {
    setShowSignOutModal(true);
    setIsDropdownOpen(false);
  };

  const handleSignOutConfirm = async () => {
    try {
      await signOut();
      setShowSignOutModal(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSignOutCancel = () => {
    setShowSignOutModal(false);
  };

  // Generate breadcrumbs based on current location
  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];

    // Always start with Dashboard
    breadcrumbs.push({
      label: 'Dashboard',
      path: '/',
      isActive: location.pathname === '/',
      isIcon: true,
    });

    // Add session breadcrumb if we're in a session
    if (pathSegments.includes('sessions') && pathSegments.length > 1) {
      const sessionId = pathSegments[pathSegments.indexOf('sessions') + 1];
      breadcrumbs.push({
        label: 'Session',
        path: `/sessions/${sessionId}`,
        isActive: true,
      });
    }

    return breadcrumbs;
  };

  // Close dropdown when clicking outside using onBlur
  const handleBlur = (event: React.FocusEvent) => {
    // Check if the new focus target is outside the dropdown
    if (!dropdownRef.current?.contains(event.relatedTarget as Node)) {
      setIsDropdownOpen(false);
    }
  };

  return (
    <nav className="relative z-40">
      <div className="px-4">
        <div className="flex justify-between items-center h-16">
          {/* Breadcrumbs */}
          <div className="flex-shrink-0">
            <nav className="flex items-center space-x-2">
              {getBreadcrumbs().map((breadcrumb, index) => (
                <React.Fragment key={breadcrumb.path}>
                  {index > 0 && (
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                  {breadcrumb.isActive ? (
                    breadcrumb.isIcon ? (
                      <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                        <img src={trayIconPng} alt="Flik" className="w-5 h-5" />
                      </div>
                    ) : (
                      <span
                        className={`text-sm font-medium ${provideTailwind(
                          'text-primary',
                        )}`}
                      >
                        {breadcrumb.label}
                      </span>
                    )
                  ) : (
                    <Link
                      to={breadcrumb.path}
                      className={`text-sm ${provideTailwind(
                        'text-secondary',
                      )} hover:${provideTailwind(
                        'text-primary',
                      )} transition-colors flex items-center`}
                    >
                      {breadcrumb.isIcon ? (
                        <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                          <img
                            src={trayIconPng}
                            alt="Flik"
                            className="w-5 h-5"
                          />
                        </div>
                      ) : (
                        breadcrumb.label
                      )}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

          {/* User info or sign in */}
          <div className="flex items-center">
            {isLoggedIn ? (
              <div className="relative" ref={dropdownRef} onBlur={handleBlur}>
                {/* Avatar button */}
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {!userData ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    initials
                  )}
                </button>

                {/* Dropdown menu */}
                {isDropdownOpen && (
                  <div
                    className={`absolute right-0 mt-2 w-64 ${provideTailwind(
                      'bg-dropdown',
                    )} rounded-md shadow-lg ${provideTailwind(
                      'border-primary',
                    )} py-1 z-50`}
                  >
                    <div
                      className={`px-4 py-2 border-b ${provideTailwind(
                        'border-secondary',
                      )}`}
                    >
                      <p
                        className={`text-sm font-medium ${provideTailwind(
                          'text-primary',
                        )}`}
                      >
                        {displayName}
                      </p>
                      {currentOrganization && (
                        <button
                          onClick={() => {
                            navigate('/organization/manage');
                            setIsDropdownOpen(false);
                          }}
                          className={`text-xs ${provideTailwind(
                            'text-tertiary',
                          )} mt-1 hover:${provideTailwind(
                            'text-primary',
                          )} transition-colors text-left w-full cursor-pointer`}
                        >
                          {whoamiData?.role === 'PowerUser' ? 'Manage' : 'View'}{' '}
                          {availableOrganizations.find(
                            (org: any) =>
                              org.organization_id === currentOrganization,
                          )?.organization_name || currentOrganization}
                        </button>
                      )}
                    </div>

                    {availableOrganizations.length > 1 && (
                      <button
                        onClick={() => {
                          setShowOrgSwitcher(true);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${provideTailwind(
                          'text-secondary',
                        )} ${provideTailwind(
                          'hover:bg-secondary',
                        )} transition-colors`}
                      >
                        Switch Organization
                      </button>
                    )}

                    <button
                      onClick={handleSignOutClick}
                      className={`w-full text-left px-4 py-2 text-sm ${provideTailwind(
                        'text-secondary',
                      )} ${provideTailwind(
                        'hover:bg-secondary',
                      )} transition-colors`}
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <Link
                  to="/signin-v2"
                  className="px-4 py-1.5 bg-white hover:bg-gray-50 text-black rounded-full transition-all font-medium text-sm flex items-center space-x-2 shadow-sm relative"
                  style={{
                    background:
                      'linear-gradient(white, white) padding-box, linear-gradient(to right, #ec4899, #8b5cf6) border-box',
                    border: '1px solid transparent',
                  }}
                >
                  Sign in
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sign Out Confirmation Modal */}
      {showSignOutModal && (
        <div
          className={`fixed inset-0 ${provideTailwind(
            'bg-primary',
          )} bg-opacity-75 flex items-center justify-center z-[9999]`}
        >
          <div
            className={`${provideTailwind(
              'bg-primary',
            )} rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Sign out
            </h3>
            <p className={`mb-6 ${provideTailwind('text-secondary')}`}>
              Are you sure you want to sign out?
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={handleSignOutCancel}
                className={`px-4 py-2 text-sm font-medium ${provideTailwind(
                  'text-secondary',
                )} ${provideTailwind(
                  'hover:bg-secondary',
                )} rounded-md transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleSignOutConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Switcher Modal */}
      {showOrgSwitcher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`bg-white rounded-lg p-6 max-w-lg w-full mx-4 ${provideTailwind(
              'bg-primary',
            )}`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className={`text-lg font-semibold ${provideTailwind(
                  'text-primary',
                )}`}
              >
                Switch Organization
              </h3>
              <button
                onClick={() => setShowOrgSwitcher(false)}
                className={`text-lg ${provideTailwind(
                  'text-secondary',
                )} hover:opacity-80`}
              >
                ×
              </button>
            </div>
            <OrganizationSwitcher />
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
